import { useCallback, useEffect, useState } from 'react'

import {
  createCurator,
  fetchCuratorProfile,
  updateCuratorProfile,
} from 'lib/profile-api'

type ProfileData = any

export const useCuratorProfile = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadProfile = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchCuratorProfile()
      setProfile(data)
    } catch (err: any) {
      setError(err?.message || 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }, [])

  const saveProfile = useCallback(
    async (updates: {
      name?: string
      bio?: string
      logo?: string | null
      telegram?: string
      discord?: string
      email?: string
      fullName?: string
      citizenship?: string
      countryOfResidence?: string
      companyName?: string
      companyRegistrationNumber?: string
      jurisdictionOfIncorporation?: string
      jurisdictionOfUBOs?: string
      primaryCountryOfBusiness?: string
      registeredAddress?: string
      website?: string
      twitterProfile?: string
      primaryWalletAddress?: string
      privateTelegramGroup?: string
      preferredContactMethod?: string
      investmentThesis?: string
      preferredVerticals?: string[]
      valueAddServices?: string[]
    }) => {
      if (!profile?.id) throw new Error('Profile not loaded')
      setSaving(true)
      setError(null)
      try {
        const merged = {
          ...profile,
          name: updates.name ?? profile.name,
          metadata: {
            ...(profile.metadata || {}),
            bio: updates.bio ?? profile.metadata?.bio ?? '',
            logo: updates.logo ?? profile.metadata?.logo ?? '',
            individualCurator: {
              ...(profile.metadata?.individualCurator || {}),
              fullName: updates.fullName ?? '',
              citizenship: updates.citizenship ?? '',
              countryOfResidence: updates.countryOfResidence ?? '',
            },
            businessEntity: {
              ...(profile.metadata?.businessEntity || {}),
              companyName: updates.companyName ?? '',
              companyRegistrationNumber:
                updates.companyRegistrationNumber ?? '',
              jurisdictionOfIncorporation:
                updates.jurisdictionOfIncorporation ?? '',
              jurisdictionOfUBOs: updates.jurisdictionOfUBOs ?? '',
              primaryCountryOfBusiness: updates.primaryCountryOfBusiness ?? '',
              registeredAddress: updates.registeredAddress ?? '',
            },
            communication: {
              ...(profile.metadata?.communication || {}),
              privateTelegramGroup: updates.privateTelegramGroup ?? '',
              preferredContactMethod: updates.preferredContactMethod ?? '',
              email: updates.email ?? '',
            },
            fundFocus: {
              ...(profile.metadata?.fundFocus || {}),
              investmentThesis: updates.investmentThesis ?? '',
              preferredVerticals: updates.preferredVerticals ?? [],
            },
            accessoryServices: {
              ...(profile.metadata?.accessoryServices || {}),
              valueAddServices: updates.valueAddServices ?? [],
            },
            wallet: {
              ...(profile.metadata?.wallet || {}),
              primaryWalletAddress: updates.primaryWalletAddress ?? '',
            },
            socialLinks: {
              ...(profile.metadata?.socialLinks || {}),
              telegram:
                updates.telegram ??
                profile.metadata?.socialLinks?.telegram ??
                '',
              discord:
                updates.discord ?? profile.metadata?.socialLinks?.discord ?? '',
              website:
                updates.website ?? profile.metadata?.socialLinks?.website ?? '',
              x:
                updates.twitterProfile ??
                profile.metadata?.socialLinks?.x ??
                '',
            },
          },
        }
        const data = await updateCuratorProfile(profile.id, merged)
        setProfile(data)
        return data
      } catch (err: any) {
        setError(err?.message || 'Failed to save profile')
        throw err
      } finally {
        setSaving(false)
      }
    },
    [profile],
  )

  const createNewCurator = useCallback(
    async (payload: {
      name: string
      bio?: string
      logo?: string | null
      tags?: string[]
      telegram?: string
      discord?: string
      email?: string
      fullName?: string
      citizenship?: string
      countryOfResidence?: string
      companyName?: string
      companyRegistrationNumber?: string
      jurisdictionOfIncorporation?: string
      jurisdictionOfUBOs?: string
      primaryCountryOfBusiness?: string
      registeredAddress?: string
      website?: string
      twitterProfile?: string
      primaryWalletAddress?: string
      privateTelegramGroup?: string
      preferredContactMethod?: string
      investmentThesis?: string
      preferredVerticals?: string[]
      valueAddServices?: string[]
    }) => {
      setSaving(true)
      setError(null)
      try {
        const merged = {
          name: payload.name,
          tags: payload.tags ?? [],
          metadata: {
            bio: payload.bio ?? '',
            logo: payload.logo ?? '',
            individualCurator: {
              fullName: payload.fullName ?? '',
              citizenship: payload.citizenship ?? '',
              countryOfResidence: payload.countryOfResidence ?? '',
            },
            businessEntity: {
              companyName: payload.companyName ?? '',
              companyRegistrationNumber:
                payload.companyRegistrationNumber ?? '',
              jurisdictionOfIncorporation:
                payload.jurisdictionOfIncorporation ?? '',
              jurisdictionOfUBOs: payload.jurisdictionOfUBOs ?? '',
              primaryCountryOfBusiness: payload.primaryCountryOfBusiness ?? '',
              registeredAddress: payload.registeredAddress ?? '',
            },
            communication: {
              privateTelegramGroup: payload.privateTelegramGroup ?? '',
              preferredContactMethod: payload.preferredContactMethod ?? '',
              email: payload.email ?? '',
            },
            fundFocus: {
              investmentThesis: payload.investmentThesis ?? '',
              preferredVerticals: payload.preferredVerticals ?? [],
            },
            accessoryServices: {
              valueAddServices: payload.valueAddServices ?? [],
            },
            wallet: {
              primaryWalletAddress: payload.primaryWalletAddress ?? '',
            },
            socialLinks: {
              telegram: payload.telegram ?? '',
              discord: payload.discord ?? '',
              website: payload.website ?? '',
              x: payload.twitterProfile ?? '',
            },
          },
        }
        const data = await createCurator(merged)
        return data
      } catch (err: any) {
        setError(err?.message || 'Failed to create curator')
        throw err
      } finally {
        setSaving(false)
      }
    },
    [],
  )

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  return {
    profile,
    loading,
    saving,
    error,
    setError,
    refresh: loadProfile,
    saveProfile,
    createNewCurator,
  }
}

export default useCuratorProfile
